import { formatDurationClock } from "./duration";

type VideoTimelineAnchorSource =
  | "video_recorded_at"
  | "session_conducted_at"
  | "unavailable";

export interface VideoTimelineAnchor {
  timestamp: string | null;
  source: VideoTimelineAnchorSource;
  isFallback: boolean;
}

const TICK_INTERVALS_SECONDS = [
  5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 10800, 14400,
  21600, 28800, 43200, 86400,
] as const;

export interface VideoTimelineTick {
  offsetSeconds: number;
  positionPercent: number;
}

export function getVideoTimelineAnchor(
  videoRecordedAt?: string | null,
  sessionConductedAt?: string | null,
): VideoTimelineAnchor {
  if (videoRecordedAt) {
    return {
      timestamp: videoRecordedAt,
      source: "video_recorded_at",
      isFallback: false,
    };
  }

  if (sessionConductedAt) {
    return {
      timestamp: sessionConductedAt,
      source: "session_conducted_at",
      isFallback: true,
    };
  }

  return {
    timestamp: null,
    source: "unavailable",
    isFallback: false,
  };
}

export function formatVideoTimelineClock(
  anchorTimestamp: string,
  offsetSeconds: number,
  options: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  },
): string {
  const anchorMs = new Date(anchorTimestamp).getTime();
  return new Intl.DateTimeFormat(undefined, options).format(
    anchorMs + offsetSeconds * 1000,
  );
}

export function formatVideoTimelineDuration(value: number): string {
  return formatDurationClock(value);
}

export function getVideoTimelineTicks(
  durationSeconds: number,
  maxTicks = 6,
): VideoTimelineTick[] {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return [{ offsetSeconds: 0, positionPercent: 0 }];
  }

  const interval =
    TICK_INTERVALS_SECONDS.find(
      (candidate) => Math.ceil(durationSeconds / candidate) + 1 <= maxTicks,
    ) ?? TICK_INTERVALS_SECONDS[TICK_INTERVALS_SECONDS.length - 1];

  const ticks: VideoTimelineTick[] = [];
  for (let offsetSeconds = 0; offsetSeconds < durationSeconds; offsetSeconds += interval) {
    ticks.push({
      offsetSeconds,
      positionPercent: (offsetSeconds / durationSeconds) * 100,
    });
  }

  ticks.push({
    offsetSeconds: durationSeconds,
    positionPercent: 100,
  });

  return ticks;
}
